import React, { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import ProductIconList from 'components/@common/ProductIconList';
import LwtTable, { ColumnProps } from 'components/@common/Table/LwtTable';
import { TFunction } from 'i18next';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ActionOption } from 'types';
import { Classroom, Student } from 'types/schema-generated-types';
import { sortGrade } from 'utilities/grade-util';

const useStyles = makeStyles(theme => {
  return {
    container: {
      marginTop: theme.spacing(3),
      marginRight: theme.spacing() * -1,
      marginLeft: theme.spacing() * -1
    },
    table: {
      maxWidth: 'unset !important'
    },
    tableBody: {
      background: 'white'
    },
    tableRow: {
      width: 30
    }
  };
});

export interface AdminClassroomDetailListViewContainerProps {
  students: Student[];
  classroom: Classroom;
  actionOptions?: Array<ActionOption>;
}

const AdminClassroomDetailListViewContainer: React.FC<AdminClassroomDetailListViewContainerProps> = ({
  students,
  classroom,
  actionOptions
}) => {
  const classes = useStyles();
  const { t } = useTranslation('AdminClassroomDetailListViewContainer');

  const getColumns: (t: TFunction) => ColumnProps<any>[] = useCallback(
    (t: TFunction) => [
      {
        id: 'firstName',
        label: t('First Name'),
        sortKey: 'firstName',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_1`}>
              <Typography>{item.firstName}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'lastName',
        label: t('Last Name'),
        sortKey: 'lastName',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_2`}>
              <Typography>{item.lastName}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'username',
        label: t('Username'),
        sortKey: 'username',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_3`}>
              <Typography>{item.username}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'password',
        label: t('Secret Code'),
        sortKey: 'password',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_4`}>
              <Typography>{item.password}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'email',
        label: t('Email Address'),
        sortKey: 'email',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_5`}>
              <Typography>{item.email}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'gradeLevel',
        label: t('Grade'),
        sortKey: 'gradeLevel',
        sortFn: (a: any, b: any): number => sortGrade(a.gradeLevel, b.gradeLevel),
        getValue(item: Student): ReactElement {
          return (
            <div key={`${item.id}_col_7`}>
              <Typography>{item.gradeLevel}</Typography>
            </div>
          );
        },
        sortable: true
      },
      {
        id: 'products',
        label: t('Products'),
        getValue(item: Student): ReactElement {
          return <ProductIconList style={{ minWidth: 120 }} licenseUsage={item.licenseUsage} />;
        },
        sortable: false
      },
      {
        id: 'actions',
        label: '',
        // eslint-disable-next-line react/display-name
        getValue(item: Student): ReactElement {
          return (
            <MoreVertMenu
              actionOptions={actionOptions ?? []}
              id={item.id}
              context={{ classroomId: classroom.id, organizationId: classroom.organizationId }}
            />
          );
        },
        sortable: false
      }
    ],
    [actionOptions, classroom.id, classroom.organizationId]
  );

  const buildRecord = useCallback((student: Student): Student => student, []);

  const buildRowData = useCallback(() => students.map(buildRecord), [students, buildRecord]);
  const columns: ColumnProps<any>[] = getColumns(t);

  return (
    <div className={classes.container}>
      <LwtTable
        columns={columns}
        data={buildRowData()}
        options={{ checkbox: false, sortable: true }}
        classes={{
          table: (): string => classes.table,
          body: (): string => classes.tableBody,
          row: (rowItem: any): string => {
            return classes.tableRow;
          }
        }}
        rowProps={(item, index) => ({
          style: {
            borderLeft: `10px solid ${classroom.color}`
          }
        })}
      />
    </div>
  );
};

export default AdminClassroomDetailListViewContainer;
